import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@/contexts/UserContext";
import { api } from "@/lib/api";
import PawSpinner from "@/components/PawSpinner";
import PomEmpty from "@/components/PomEmpty";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const FILTERS = [
  "All",
  "Has generated photo",
  "Food",
  "Movement",
  "Learn",
  "Experience",
  "Other",
];

export default function Memories() {
  const { userId } = useUser();
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [showGenerated, setShowGenerated] = useState(false);

  const { data: memories = [], isLoading } = useQuery({
    queryKey: ["memories", userId, activeFilter],
    queryFn: () => {
      const hasGenerated =
        activeFilter === "Has generated photo" ? true : null;
      const category = !["All", "Has generated photo"].includes(activeFilter)
        ? activeFilter
        : null;
      return api.listMemories(userId, hasGenerated, category).then((r) => r.data);
    },
    enabled: !!userId,
  });

  if (isLoading)
    return (
      <div className="flex justify-center py-20">
        <PawSpinner />
      </div>
    );

  return (
    <div className="space-y-6 py-2" data-testid="memories-screen">
      <h1 className="font-heading text-2xl text-text-primary">Memories</h1>

      {/* Filters */}
      <div
        className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
        data-testid="memory-filters"
      >
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            className={`px-4 py-1.5 rounded-pill text-sm font-medium whitespace-nowrap transition-colors ${
              activeFilter === f
                ? "bg-blush text-white"
                : "bg-white text-text-secondary border border-border-pink hover:bg-cream"
            }`}
            data-testid={`memory-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Gallery Grid */}
      {memories.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {memories.map((memory) => (
            <div
              key={memory.id}
              onClick={() => {
                setSelectedMemory(memory);
                setShowGenerated(!!(memory.generated_photo_urls?.length || memory.generated_photo_url));
              }}
              className="bg-white rounded-card shadow-soft overflow-hidden cursor-pointer card-hover"
              data-testid={`memory-card-${memory.id}`}
            >
              {(memory.generated_photo_urls?.[0] || memory.generated_photo_url || memory.photo_url) ? (
                <div className="aspect-square bg-cream">
                  <img
                    src={api.getFileUrl(
                      memory.generated_photo_urls?.[0] || memory.generated_photo_url || memory.photo_url
                    )}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-square bg-cream flex items-center justify-center">
                  <Camera className="text-border-pink" size={32} />
                </div>
              )}
              <div className="p-3">
                <p className="text-sm font-medium text-text-primary truncate">
                  {memory.bucket_item?.title || "Memory"}
                </p>
                <div className="flex items-center gap-1.5 mt-1">
                  {memory.bucket_item?.category && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-pill bg-cream text-text-secondary">
                      {memory.bucket_item.category}
                    </span>
                  )}
                  <span className="text-[10px] text-text-secondary">
                    {new Date(memory.completed_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <PomEmpty message="No memories yet. Complete an adventure to create one" />
      )}

      {/* Detail Modal */}
      <Dialog
        open={!!selectedMemory}
        onOpenChange={() => setSelectedMemory(null)}
      >
        <DialogContent className="rounded-card bg-white border-border-pink max-w-md mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-text-primary">
              {selectedMemory?.bucket_item?.title || "Memory"}
            </DialogTitle>
            <DialogDescription className="text-text-secondary text-sm">
              {selectedMemory &&
                new Date(selectedMemory.completed_at).toLocaleDateString(
                  "en-US",
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }
                )}
            </DialogDescription>
          </DialogHeader>

          {selectedMemory && (
            <div className="space-y-4 mt-2">
              {selectedMemory.generated_photo_url &&
                selectedMemory.photo_url && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowGenerated(false)}
                      className={`px-3 py-1 rounded-pill text-xs font-medium transition-colors ${
                        !showGenerated
                          ? "bg-blush text-white"
                          : "bg-cream text-text-secondary"
                      }`}
                      data-testid="toggle-original-photo"
                    >
                      Original
                    </button>
                    <button
                      onClick={() => setShowGenerated(true)}
                      className={`px-3 py-1 rounded-pill text-xs font-medium transition-colors ${
                        showGenerated
                          ? "bg-blush text-white"
                          : "bg-cream text-text-secondary"
                      }`}
                      data-testid="toggle-generated-photo"
                    >
                      With character
                    </button>
                  </div>
                )}

              {(selectedMemory.photo_url ||
                selectedMemory.generated_photo_url) && (
                <img
                  src={api.getFileUrl(
                    showGenerated && selectedMemory.generated_photo_url
                      ? selectedMemory.generated_photo_url
                      : selectedMemory.photo_url ||
                          selectedMemory.generated_photo_url
                  )}
                  alt=""
                  className="w-full rounded-input"
                />
              )}

              {selectedMemory.journal_liked && (
                <div>
                  <p className="text-xs text-text-secondary mb-1 font-medium">
                    What was loved
                  </p>
                  <p className="text-sm text-text-primary">
                    {selectedMemory.journal_liked}
                  </p>
                </div>
              )}
              {selectedMemory.journal_disliked && (
                <div>
                  <p className="text-xs text-text-secondary mb-1 font-medium">
                    What wasn't loved
                  </p>
                  <p className="text-sm text-text-primary">
                    {selectedMemory.journal_disliked}
                  </p>
                </div>
              )}
              {selectedMemory.journal_would_repeat && (
                <div>
                  <p className="text-xs text-text-secondary mb-1 font-medium">
                    Would repeat?
                  </p>
                  <span className="text-sm text-text-primary capitalize bg-cream px-3 py-1 rounded-pill">
                    {selectedMemory.journal_would_repeat}
                  </span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Camera(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={props.size || 24}
      height={props.size || 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={props.className}
    >
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );
}
